import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useUploadPolling } from '../../contexts/UploadPollingContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Label from '../ui/Label';
import Input from '../ui/Input';
import { FunctionalSelect, SelectItem } from '../ui/Select';
import ErrorDialog from '../ui/ErrorDialog';
import FileDropzone from '../ui/FileDropzone';
import { api } from '../../services/authService';
import { MAX_UPLOAD_FILE_BYTES, UNIVERSE_COLUMN_OPTIONS } from '../../utils/uploadConstants';
import { formatS3DirectUploadError, uploadBrowserFileToS3 } from '../../utils/s3PresignedUpload';

/**
 * Same upload capability the Upload page used to offer for Fast-ID: File / Text / S3, now on its own page.
 * Organization selection lives on the page (shared with the report list below), passed down as props.
 */
export default function FastIdUploadForm({
  organizations,
  selectedOrganization,
  organizationsLoading,
  onUploadStarted,
}) {
  const { startUploadJobPolling } = useUploadPolling();

  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [useApiKey, setUseApiKey] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const [fastIdInputMode, setFastIdInputMode] = useState('file');
  const [fastIdHashesText, setFastIdHashesText] = useState('');
  const [fastIdS3Path, setFastIdS3Path] = useState('');
  const [selectedFastIdFile, setSelectedFastIdFile] = useState(null);
  const [fastIdEmailMd5Column, setFastIdEmailMd5Column] = useState('1');
  const [fastIdEmailFormat, setFastIdEmailFormat] = useState('md5');

  // Selecting a different org while a file is staged would upload it against the wrong org.
  useEffect(() => {
    setSelectedFastIdFile(null);
  }, [selectedOrganization]);

  const buildHeaders = async () => {
    const headers = { 'Content-Type': 'multipart/form-data' };
    if (useApiKey && apiKeyInput.trim()) {
      headers['Authorization'] = `apikey ${apiKeyInput.trim()}`;
    }
    return headers;
  };

  const handleFileDropzoneReject = (message) => {
    setErrorMessage(message);
    setShowErrorDialog(true);
  };

  const clearFastIdFileInput = () => {
    const fileInput = document.getElementById('uploadFileFastId');
    if (fileInput) fileInput.value = '';
    setSelectedFastIdFile(null);
  };

  const handleFastIdFileSelected = (file) => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before uploading a file.');
      setShowErrorDialog(true);
      return;
    }
    if (useApiKey && !apiKeyInput.trim()) {
      setErrorMessage('Please enter an API key.');
      setShowErrorDialog(true);
      return;
    }
    if (file.size > MAX_UPLOAD_FILE_BYTES) {
      setErrorMessage('Fast-ID file size must be 100 GB or less. For larger files, use Fast-ID → S3.');
      setShowErrorDialog(true);
      return;
    }
    setSelectedFastIdFile(file);
  };

  const handleFastIdUpload = async () => {
    const file = selectedFastIdFile;
    if (!file) return;
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before uploading a file.');
      setShowErrorDialog(true);
      return;
    }
    if (useApiKey && !apiKeyInput.trim()) {
      setErrorMessage('Please enter an API key.');
      setShowErrorDialog(true);
      return;
    }
    if (file.size > MAX_UPLOAD_FILE_BYTES) {
      setErrorMessage('Fast-ID file size must be 100 GB or less. For larger files, use Fast-ID → S3.');
      setShowErrorDialog(true);
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const headers = await buildHeaders();
      const { s3_path } = await uploadBrowserFileToS3({
        presignPath: '/api/v2/upload_fast_id_presign',
        uploadType: 'fast_id',
        file,
        organization: selectedOrganization,
        headers,
      });
      const formData = new FormData();
      formData.append('s3_path', s3_path);
      formData.append('organization', selectedOrganization);
      formData.append('email_md5_column', fastIdEmailMd5Column);
      formData.append('email_format', fastIdEmailFormat);
      const res = await api.post('/api/v2/upload_fast_id_s3', formData, { headers });
      const data = res.data;
      setUploadResult(data);
      if (data.execution_id) startUploadJobPolling(data.execution_id, 'fast_id');
      toast.success('Fast-ID upload started. Check the list below.');
      clearFastIdFileInput();
      onUploadStarted?.();
    } catch (error) {
      console.error('Fast-ID upload error:', error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      const detailStr =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d?.msg ?? d).join(' ')
            : detail != null
              ? String(detail)
              : '';
      let msg = detailStr.trim() || error.message || 'Failed to upload Fast-ID file.';
      const s3Hint = formatS3DirectUploadError(error);
      if (s3Hint) {
        msg = s3Hint;
      } else if (status === 413) {
        msg =
          'The upload was rejected (HTTP 413). A load balancer, CDN, or reverse proxy in front of the API often enforces a limit below 100 GB, even for smaller files. Use Fast-ID input → S3 and enter s3://bucket/path to your CSV instead.';
      } else if (!error.response) {
        if (error.code === 'ECONNABORTED' || String(error.message || '').toLowerCase().includes('timeout')) {
          msg =
            'The upload timed out or was cancelled. For large files, use Fast-ID input → S3 and enter s3://bucket/path to your CSV instead.';
        } else if (error.message === 'Network Error') {
          msg =
            'Network error while uploading. For large files, use Fast-ID input → S3 and enter s3://bucket/path to your CSV instead.';
        }
      }
      setErrorMessage(msg);
      setShowErrorDialog(true);
    } finally {
      setUploading(false);
    }
  };

  const handleFastIdHashesSubmit = async () => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before submitting.');
      setShowErrorDialog(true);
      return;
    }
    if (useApiKey && !apiKeyInput.trim()) {
      setErrorMessage('Please enter an API key.');
      setShowErrorDialog(true);
      return;
    }
    const trimmed = (fastIdHashesText || '').trim();
    if (!trimmed) {
      setErrorMessage(
        fastIdEmailFormat === 'email'
          ? 'Please enter at least one email address (comma-separated).'
          : 'Please enter at least one MD5 hash (comma-separated).'
      );
      setShowErrorDialog(true);
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const headers = await buildHeaders();
      const formData = new FormData();
      formData.append('hashes', trimmed);
      formData.append('organization', selectedOrganization);
      formData.append('email_format', fastIdEmailFormat);
      const res = await api.post('/api/v2/upload_fast_id_hashes', formData, { headers });
      const data = res.data;
      setUploadResult(data);
      if (data.execution_id) startUploadJobPolling(data.execution_id, 'fast_id');
      toast.success('Fast-ID hashes upload started. Check the list below.');
      setFastIdHashesText('');
      onUploadStarted?.();
    } catch (error) {
      console.error('Fast-ID hashes upload error:', error);
      setErrorMessage(error.response?.data?.detail || error.message || 'Failed to upload Fast-ID hashes.');
      setShowErrorDialog(true);
    } finally {
      setUploading(false);
    }
  };

  const handleFastIdS3Submit = async () => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization before submitting.');
      setShowErrorDialog(true);
      return;
    }
    if (useApiKey && !apiKeyInput.trim()) {
      setErrorMessage('Please enter an API key.');
      setShowErrorDialog(true);
      return;
    }
    const trimmed = (fastIdS3Path || '').trim();
    if (!trimmed) {
      setErrorMessage('Please enter an S3 path.');
      setShowErrorDialog(true);
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const headers = await buildHeaders();
      const formData = new FormData();
      formData.append('s3_path', trimmed);
      formData.append('organization', selectedOrganization);
      formData.append('email_md5_column', fastIdEmailMd5Column);
      formData.append('email_format', fastIdEmailFormat);
      const res = await api.post('/api/v2/upload_fast_id_s3', formData, { headers });
      const data = res.data;
      setUploadResult(data);
      if (data.execution_id) startUploadJobPolling(data.execution_id, 'fast_id');
      toast.success('Fast-ID upload queued from S3 (copy may run in the background). Check the list below.');
      onUploadStarted?.();
    } catch (error) {
      console.error('Fast-ID S3 path error:', error);
      setErrorMessage(error.response?.data?.detail || error.message || 'Failed to start Fast-ID upload from S3.');
      setShowErrorDialog(true);
    } finally {
      setUploading(false);
    }
  };

  const disableInputs =
    uploading || !selectedOrganization || organizationsLoading || organizations.length === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload FastID File</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!organizationsLoading && organizations.length === 0 && (
          <p className="text-sm text-amber-600">
            No organizations available. Please contact an administrator to be added to an organization.
          </p>
        )}

        <div className="space-y-2">
          <Label>Fast-ID input</Label>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="fastIdInputMode"
                value="file"
                checked={fastIdInputMode === 'file'}
                onChange={() => {
                  setFastIdInputMode('file');
                  setSelectedFastIdFile(null);
                }}
                disabled={uploading}
                className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300"
              />
              <span className="text-sm font-medium">File</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="fastIdInputMode"
                value="text"
                checked={fastIdInputMode === 'text'}
                onChange={() => {
                  setFastIdInputMode('text');
                  setSelectedFastIdFile(null);
                }}
                disabled={uploading}
                className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300"
              />
              <span className="text-sm font-medium">Text</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="fastIdInputMode"
                value="s3"
                checked={fastIdInputMode === 's3'}
                onChange={() => {
                  setFastIdInputMode('s3');
                  setSelectedFastIdFile(null);
                }}
                disabled={uploading}
                className="h-4 w-4 text-[#0062FB] focus:ring-[#0062FB] border-gray-300"
              />
              <span className="text-sm font-medium">S3</span>
            </label>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="fastIdUseApiKey"
              checked={useApiKey}
              onChange={(e) => {
                setUseApiKey(e.target.checked);
                if (!e.target.checked) setApiKeyInput('');
              }}
              disabled={uploading}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <Label htmlFor="fastIdUseApiKey" className="cursor-pointer">
              Use API Key
            </Label>
          </div>
        </div>

        {useApiKey && (
          <div className="space-y-2">
            <Label htmlFor="fastIdApiKeyInput">API Key</Label>
            <Input
              id="fastIdApiKeyInput"
              type="password"
              autoComplete="off"
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              placeholder="Enter API key"
              disabled={uploading}
            />
          </div>
        )}

        {fastIdInputMode === 'file' && (
          <div className="space-y-2">
            <FileDropzone
              inputId="uploadFileFastId"
              label="Select Fast-ID CSV"
              accept=".csv"
              maxSize={MAX_UPLOAD_FILE_BYTES}
              tooLargeHint="Use Fast-ID input → S3 and enter s3://bucket/path to your CSV instead."
              onReject={handleFileDropzoneReject}
              disabled={disableInputs || (useApiKey && !apiKeyInput.trim())}
              onFileSelect={handleFastIdFileSelected}
            />
            {uploading && <p className="text-sm text-blue-600">Uploading file...</p>}
            {!organizationsLoading && organizations.length === 0 && (
              <p className="text-sm text-amber-600">
                File upload is disabled because you are not associated with any organizations.
              </p>
            )}
            {!organizationsLoading && organizations.length > 0 && (
              <p className="text-sm text-gray-500">
                Select a FastID file to upload (CSV or JSON).
              </p>
            )}
            {selectedFastIdFile && (
              <div className="space-y-4 border-t border-gray-200 pt-4 mt-4">
                <p className="text-sm text-gray-600">
                  Selected: <span className="font-medium">{selectedFastIdFile.name}</span>
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <FunctionalSelect
                      label="Email/md5 column"
                      id="fastIdEmailMd5Column"
                      value={fastIdEmailMd5Column}
                      onValueChange={setFastIdEmailMd5Column}
                      placeholder="Select"
                      className="w-full"
                      disabled={uploading}
                    >
                      {UNIVERSE_COLUMN_OPTIONS.map((n) => (
                        <SelectItem key={n} value={n}>
                          {n}
                        </SelectItem>
                      ))}
                    </FunctionalSelect>
                  </div>
                  <div className="space-y-2">
                    <FunctionalSelect
                      label="Email format"
                      id="fastIdEmailFormat"
                      value={fastIdEmailFormat}
                      onValueChange={setFastIdEmailFormat}
                      placeholder="Select"
                      className="w-full"
                      disabled={uploading}
                    >
                      <SelectItem value="md5">md5</SelectItem>
                      <SelectItem value="email">email</SelectItem>
                    </FunctionalSelect>
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleFastIdUpload}
                    disabled={disableInputs || (useApiKey && !apiKeyInput.trim())}
                    className="px-4 py-2 border border-[#0062FB] bg-[#0062FB] text-white rounded-md hover:bg-[#0052E0] hover:border-[#0052E0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {uploading ? 'Uploading…' : 'Upload'}
                  </button>
                  <button
                    type="button"
                    onClick={clearFastIdFileInput}
                    disabled={uploading}
                    className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {fastIdInputMode === 's3' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fastIdS3Path">S3 Path</Label>
              <input
                id="fastIdS3Path"
                type="text"
                value={fastIdS3Path}
                onChange={(e) => setFastIdS3Path(e.target.value)}
                placeholder="s3://<bucket>/<prefix>"
                disabled={disableInputs || (useApiKey && !apiKeyInput.trim())}
                className="w-full px-3 py-2 border border-hairline rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#18181B]/10 focus:ring-offset-0 disabled:opacity-50 disabled:bg-gray-50 text-sm font-mono"
                autoComplete="off"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <FunctionalSelect
                  label="Email/md5 column"
                  id="fastIdS3EmailMd5Column"
                  value={fastIdEmailMd5Column}
                  onValueChange={setFastIdEmailMd5Column}
                  placeholder="Select"
                  className="w-full"
                  disabled={uploading}
                >
                  {UNIVERSE_COLUMN_OPTIONS.map((n) => (
                    <SelectItem key={n} value={n}>
                      {n}
                    </SelectItem>
                  ))}
                </FunctionalSelect>
              </div>
              <div className="space-y-2">
                <FunctionalSelect
                  label="Email format"
                  id="fastIdS3EmailFormat"
                  value={fastIdEmailFormat}
                  onValueChange={setFastIdEmailFormat}
                  placeholder="Select"
                  className="w-full"
                  disabled={uploading}
                >
                  <SelectItem value="md5">md5</SelectItem>
                  <SelectItem value="email">email</SelectItem>
                </FunctionalSelect>
              </div>
            </div>
            <p className="text-sm text-gray-500">
              Use an existing CSV in S3 that Databricks can read (recommended for files over 100 GB). Column and format
              match file upload and are passed to validation in Databricks.
            </p>
            <button
              type="button"
              onClick={handleFastIdS3Submit}
              disabled={disableInputs || !(fastIdS3Path || '').trim() || (useApiKey && !apiKeyInput.trim())}
              className="px-4 py-2 border border-[#0062FB] bg-[#0062FB] text-white rounded-md hover:bg-[#0052E0] hover:border-[#0052E0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Submit
            </button>
          </div>
        )}

        {fastIdInputMode === 'text' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Text is uploaded as one value per line (single column). Same format option as file and S3 upload
              (Databricks validate_universe).
            </p>
            <div className="space-y-2">
              <Label htmlFor="fastIdHashes">
                {fastIdEmailFormat === 'email'
                  ? 'Paste comma-separated email addresses'
                  : 'Paste comma-separated MD5 hashes'}
              </Label>
              <textarea
                id="fastIdHashes"
                value={fastIdHashesText}
                onChange={(e) => setFastIdHashesText(e.target.value)}
                placeholder={
                  fastIdEmailFormat === 'email'
                    ? 'e.g. user@example.com, other@yahoo.com'
                    : 'e.g. abc123..., def456... (32 hex chars each)'
                }
                rows={5}
                disabled={disableInputs || (useApiKey && !apiKeyInput.trim())}
                className="w-full px-3 py-2 border border-hairline rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#18181B]/10 focus:ring-offset-0 disabled:opacity-50 disabled:bg-gray-50 text-sm font-mono"
              />
            </div>
            <div className="space-y-2 max-w-sm">
              <FunctionalSelect
                label="Email format"
                id="fastIdTextEmailFormat"
                value={fastIdEmailFormat}
                onValueChange={setFastIdEmailFormat}
                placeholder="Select"
                className="w-full"
                disabled={uploading}
              >
                <SelectItem value="md5">md5</SelectItem>
                <SelectItem value="email">email</SelectItem>
              </FunctionalSelect>
            </div>
            <button
              type="button"
              onClick={handleFastIdHashesSubmit}
              disabled={disableInputs || !(fastIdHashesText || '').trim() || (useApiKey && !apiKeyInput.trim())}
              className="px-4 py-2 border border-[#0062FB] bg-[#0062FB] text-white rounded-md hover:bg-[#0052E0] hover:border-[#0052E0] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Submit
            </button>
          </div>
        )}

        {uploadResult && useApiKey && apiKeyInput.trim() && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Query:</h3>
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4 overflow-auto">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                {(() => {
                  const baseUrl = window.location.origin;
                  const apiKeyValue = '<API_KEY_TOKEN>';
                  const fileName =
                    document.getElementById('uploadFileFastId')?.files?.[0]?.name || '<filename>';
                  const isLocalhost = baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1');
                  const isHttps = baseUrl.startsWith('https://');
                  const kFlag = isLocalhost && isHttps ? ' -k' : '';
                  const h = `  -H "Authorization: apikey ${apiKeyValue}"`;
                  const s3PostNote =
                    '# 2) POST to post_url from step 1: include every form_fields key=value, then multipart part "file" (last) with the bytes.';

                  if (fastIdInputMode === 's3') {
                    return `curl -X POST "${baseUrl}/api/v2/upload_fast_id_s3"${kFlag} \\\n${h} \\\n  -F "s3_path=s3://your-bucket/your-prefix" \\\n  -F "organization=${selectedOrganization}" \\\n  -F "email_md5_column=${fastIdEmailMd5Column}" \\\n  -F "email_format=${fastIdEmailFormat}"`;
                  }
                  if (fastIdInputMode === 'text') {
                    return `curl -X POST "${baseUrl}/api/v2/upload_fast_id_hashes"${kFlag} \\\n${h} \\\n  -F "hashes=<comma-separated-md5-or-emails>" \\\n  -F "organization=${selectedOrganization}" \\\n  -F "email_format=${fastIdEmailFormat}"`;
                  }
                  return [
                    '# 1) Presigned POST policy',
                    `curl -s -X POST "${baseUrl}/api/v2/upload_fast_id_presign"${kFlag} \\`,
                    `${h} \\`,
                    `  -F "organization=${selectedOrganization}" \\`,
                    `  -F "file_name=${fileName}"`,
                    s3PostNote,
                    '# 3) Start Fast-ID job',
                    `curl -X POST "${baseUrl}/api/v2/upload_fast_id_s3"${kFlag} \\`,
                    `${h} \\`,
                    '  -F "s3_path=<s3_path from step 1 response>" \\',
                    `  -F "organization=${selectedOrganization}" \\`,
                    `  -F "email_md5_column=${fastIdEmailMd5Column}" \\`,
                    `  -F "email_format=${fastIdEmailFormat}"`,
                  ].join('\n');
                })()}
              </pre>
            </div>
          </div>
        )}

        {uploadResult && useApiKey && apiKeyInput.trim() && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Result:</h3>
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4 overflow-auto">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                {JSON.stringify(uploadResult, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </CardContent>

      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        message={errorMessage}
      />
    </Card>
  );
}
